import {
	HTTP_METHODS,
	HTTP_PRESET_INFO,
	HTTP_PRESETS,
	type HttpPreset,
	isHttpPreset,
	MAX_HTTP_HEADERS,
	MAX_HTTP_MAPPINGS,
	MAX_HTTP_TIMEOUT_SECONDS,
} from "@velizhanin-dev/bot-scenario"
import { Plus, Trash2 } from "lucide-react"
import { Button } from "@/shared/ui/button"
import { Field, IssueText } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { NativeSelect } from "@/shared/ui/native-select"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { TextWithVariables } from "@/shared/ui/text-with-variables"
import { VariableSelect } from "@/shared/ui/variable-select"
import type { Config, EditorProps } from "../editor-props"
import { parseOptionalNumber, readNumber, readString, withField } from "../editor-props"
import { issuesAt } from "../issues-text"
import { LoadSwap } from "@/shared/ui/load-swap"

// biome-ignore lint/suspicious/noTemplateCurlyInString: так в конструкторе записываются переменные
const JSON_BODY_EXAMPLE = '{"name": "${firstName}", "phone": "${phone}"}'

interface HeaderDraft {
	name: string
	value: string
}

interface MappingDraft {
	path: string
	variable: string
}

type RequestMode = "custom" | "preset"

const MODE_OPTIONS = [
	{ value: "custom", label: "Свой запрос" },
	{ value: "preset", label: "Готовый" },
] as const

/** Готовому запросу адрес, метод, заголовки и тело не нужны: их подставляет API. */
function toPreset(config: Config, preset: HttpPreset): Config {
	const { body: _body, ...rest } = config
	return { ...rest, preset, method: "GET", url: "", headers: [] }
}

function toCustom(config: Config): Config {
	const { preset: _preset, presetInput: _input, ...rest } = config
	return rest
}

export function HttpRequestEditor({ config, onChange, issues, draft }: EditorProps) {
	const preset = isHttpPreset(config.preset) ? config.preset : null
	const mappings = (Array.isArray(config.mappings) ? config.mappings : []) as MappingDraft[]

	return (
		<div className="space-y-4">
			<SegmentedControl
				aria-label="Вид запроса"
				size="sm"
				options={MODE_OPTIONS}
				value={preset ? "preset" : "custom"}
				onChange={(mode: RequestMode | null) => {
					if (mode === "preset" && !preset) onChange(toPreset(config, HTTP_PRESETS[0]))
					if (mode === "custom" && preset) onChange(toCustom(config))
				}}
			/>

			<LoadSwap loading={false} id={preset ? "preset" : "custom"}>
				<div className="space-y-4">
					{preset ? (
						<PresetFields
							preset={preset}
							config={config}
							onChange={onChange}
							issues={issues}
							draft={draft}
						/>
					) : (
						<CustomFields config={config} onChange={onChange} issues={issues} draft={draft} />
					)}
				</div>
			</LoadSwap>

			<ResponseFields
				config={config}
				onChange={onChange}
				issues={issues}
				draft={draft}
				mappings={mappings}
				suggestedPaths={preset ? HTTP_PRESET_INFO[preset].fields.map((field) => field.path) : []}
			/>
		</div>
	)
}

type FieldsProps = Pick<EditorProps, "config" | "onChange" | "issues" | "draft">

function PresetFields({
	preset,
	config,
	onChange,
	issues,
	draft,
}: FieldsProps & { preset: HttpPreset }) {
	const info = HTTP_PRESET_INFO[preset]
	return (
		<>
			<Field label="Запрос" issues={issuesAt(issues, "preset")}>
				<NativeSelect
					aria-label="Готовый запрос"
					value={preset}
					onChange={(event) => {
						const next = event.target.value as HttpPreset
						onChange(
							HTTP_PRESET_INFO[next].input
								? { ...config, preset: next }
								: withField({ ...config, preset: next }, "presetInput", undefined),
						)
					}}
				>
					{HTTP_PRESETS.map((value) => (
						<option key={value} value={value}>
							{HTTP_PRESET_INFO[value].label}
						</option>
					))}
				</NativeSelect>
			</Field>
			<p className="text-xs text-muted-foreground">
				{info.description}. Выполняется от имени человека, который пишет боту; ключи и адреса не
				нужны.
			</p>

			{info.input ? (
				<Field label={info.input.label} issues={issuesAt(issues, "presetInput")}>
					<TextWithVariables
						multiline={false}
						placeholder={info.input.placeholder}
						value={readString(config, "presetInput")}
						onChange={(value) => onChange(withField(config, "presetInput", value || undefined))}
						variables={draft.variables}
					/>
				</Field>
			) : null}

			<div className="rounded-xl bg-secondary/40 p-3 text-xs text-muted-foreground">
				<p className="font-medium text-foreground">Что вернёт</p>
				<ul className="mt-1 space-y-0.5">
					{info.fields.map((field) => (
						<li key={field.path}>
							<code className="text-foreground">{field.path}</code>: {field.description}
						</li>
					))}
				</ul>
				{info.statuses.length > 0 ? (
					<>
						<p className="mt-2 font-medium text-foreground">Коды ответа при ошибке</p>
						<ul className="mt-1 space-y-0.5">
							{info.statuses.map((item) => (
								<li key={item.status}>
									<code className="text-foreground">{item.status}</code>: {item.description}
								</li>
							))}
						</ul>
					</>
				) : null}
			</div>
		</>
	)
}

function CustomFields({ config, onChange, issues, draft }: FieldsProps) {
	const method = readString(config, "method") || "GET"
	const headers = (Array.isArray(config.headers) ? config.headers : []) as HeaderDraft[]
	const withBody = method !== "GET" && method !== "DELETE"

	return (
		<>
			<div className="grid grid-cols-[6rem_1fr] gap-2">
				<NativeSelect
					aria-label="Метод"
					value={method}
					onChange={(event) => {
						const next = event.target.value
						const base = { ...config, method: next }
						onChange(
							next === "GET" || next === "DELETE" ? withField(base, "body", undefined) : base,
						)
					}}
				>
					{HTTP_METHODS.map((value) => (
						<option key={value} value={value}>
							{value}
						</option>
					))}
				</NativeSelect>
				<TextWithVariables
					multiline={false}
					placeholder="https://api.example.com/leads"
					value={readString(config, "url")}
					onChange={(url) => onChange({ ...config, url })}
					variables={draft.variables}
				/>
			</div>
			<IssueText issues={[...issuesAt(issues, "url"), ...issuesAt(issues, "method")]} />
			<p className="text-xs text-muted-foreground">
				Только https. Переменные в адресе можно ставить только после домена. Во внутреннюю сеть
				сервера запрос не уйдёт.
			</p>

			<Field label="Заголовки" issues={issuesAt(issues, "headers", true)}>
				<div className="space-y-2">
					{headers.map((header, index) => (
						<div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-1">
							<Input
								aria-label={`Имя заголовка ${index + 1}`}
								placeholder="Authorization"
								value={header.name}
								onChange={(event) =>
									onChange({
										...config,
										headers: headers.map((item, i) =>
											i === index ? { ...item, name: event.target.value } : item,
										),
									})
								}
							/>
							<Input
								aria-label={`Значение заголовка ${index + 1}`}
								placeholder="Bearer …"
								value={header.value}
								onChange={(event) =>
									onChange({
										...config,
										headers: headers.map((item, i) =>
											i === index ? { ...item, value: event.target.value } : item,
										),
									})
								}
							/>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								aria-label={`Удалить заголовок ${index + 1}`}
								onClick={() =>
									onChange({ ...config, headers: headers.filter((_, i) => i !== index) })
								}
							>
								<Trash2 />
							</Button>
						</div>
					))}
					{headers.length < MAX_HTTP_HEADERS ? (
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() =>
								onChange({ ...config, headers: [...headers, { name: "", value: "" }] })
							}
						>
							<Plus />
							Заголовок
						</Button>
					) : null}
				</div>
			</Field>

			{withBody ? (
				<Field
					label="Тело запроса (JSON)"
					issues={issuesAt(issues, "body")}
					hint="Значения переменных подставляются уже экранированными под JSON-строку: пишите их в кавычках."
				>
					<TextWithVariables
						rows={5}
						placeholder={JSON_BODY_EXAMPLE}
						value={readString(config, "body")}
						onChange={(body) => onChange(withField(config, "body", body || undefined))}
						variables={draft.variables}
					/>
				</Field>
			) : null}

			<Field label="Ждать ответа, секунд" issues={issuesAt(issues, "timeoutSeconds")}>
				<Input
					type="number"
					min={1}
					max={MAX_HTTP_TIMEOUT_SECONDS}
					aria-label="Таймаут запроса"
					placeholder="10"
					value={readNumber(config, "timeoutSeconds") ?? ""}
					onChange={(event) =>
						onChange(withField(config, "timeoutSeconds", parseOptionalNumber(event.target.value)))
					}
				/>
			</Field>
		</>
	)
}

function ResponseFields({
	config,
	onChange,
	issues,
	draft,
	mappings,
	suggestedPaths,
}: FieldsProps & { mappings: MappingDraft[]; suggestedPaths: string[] }) {
	const unusedPaths = suggestedPaths.filter((path) => !mappings.some((item) => item.path === path))

	return (
		<>
			<Field label="Код ответа сохранить в" issues={issuesAt(issues, "saveStatusTo")}>
				<VariableSelect
					aria-label="Переменная для кода ответа"
					value={readString(config, "saveStatusTo")}
					onChange={(name) => onChange(withField(config, "saveStatusTo", name || undefined))}
					variables={draft.variables}
					emptyLabel="Не сохранять"
				/>
			</Field>
			<Field label="Ответ целиком сохранить в" issues={issuesAt(issues, "saveBodyTo")}>
				<VariableSelect
					aria-label="Переменная для тела ответа"
					value={readString(config, "saveBodyTo")}
					onChange={(name) => onChange(withField(config, "saveBodyTo", name || undefined))}
					variables={draft.variables}
					emptyLabel="Не сохранять"
				/>
			</Field>

			<Field
				label="Поля ответа в переменные"
				issues={issuesAt(issues, "mappings", true)}
				hint="Путь в JSON через точку: data.items.0.title"
			>
				<div className="space-y-2">
					{mappings.map((mapping, index) => (
						<div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-1">
							<Input
								aria-label={`Путь поля ${index + 1}`}
								placeholder="data.id"
								value={mapping.path}
								onChange={(event) =>
									onChange({
										...config,
										mappings: mappings.map((item, i) =>
											i === index ? { ...item, path: event.target.value } : item,
										),
									})
								}
							/>
							<VariableSelect
								aria-label={`Переменная поля ${index + 1}`}
								value={mapping.variable}
								onChange={(variable) =>
									onChange({
										...config,
										mappings: mappings.map((item, i) =>
											i === index ? { ...item, variable } : item,
										),
									})
								}
								variables={draft.variables}
							/>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								aria-label={`Удалить поле ${index + 1}`}
								onClick={() =>
									onChange({ ...config, mappings: mappings.filter((_, i) => i !== index) })
								}
							>
								<Trash2 />
							</Button>
						</div>
					))}
					{mappings.length < MAX_HTTP_MAPPINGS ? (
						<div className="flex flex-wrap gap-1">
							{unusedPaths.map((path) => (
								<Button
									key={path}
									type="button"
									variant="outline"
									size="sm"
									onClick={() =>
										onChange({ ...config, mappings: [...mappings, { path, variable: "" }] })
									}
								>
									<Plus />
									{path}
								</Button>
							))}
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() =>
									onChange({ ...config, mappings: [...mappings, { path: "", variable: "" }] })
								}
							>
								<Plus />
								Поле
							</Button>
						</div>
					) : null}
				</div>
			</Field>
		</>
	)
}
